import React, { useState, useEffect, useRef } from "react";
import Utils from "../../utility";
import { getInspectionReport } from "../../services/zbrainConnectorService";
import { useLocation, useNavigate } from "react-router-dom";
import Report from "./report";

const AIReportListComponent = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const navigate = useNavigate();
  const [report, setReport] = useState({});
  const [date, setDate] = useState({
    startDate: queryParams.get("fromDate") || null,
    endDate: queryParams.get("toDate") || null
  });
  const [loading, setLoading] = useState(false);
  const [selectTime, setSelectTime] = useState(
    queryParams.get("selectTime") || "All"
  );
  const [withHours, setWithHours] = useState(
    queryParams.get("withWorkingHours") || "All"
  );
  const [selectedVersion, setSelectedVersion] = useState(
    queryParams.get("version") || "Latest"
  );
  const [processedDate, setProcessedDate] = useState({
    startDate: queryParams.get("processedOnFrom") || null,
    endDate: queryParams.get("processedOnTo") || null
  });

  const [feedbackDate, setFeebackDate] = useState({
    startDate: queryParams.get("feedbackFromDate") || null,
    endDate: queryParams.get("feedbackToDate") || null
  });

  const [selectProcessedTime, setSelectProcessedTime] = useState(
    queryParams.get("processedTime") || "Last 14 days"
  );

  const [selectFeedbackTime, setSelectFeedbackTime] = useState(
    queryParams.get("feedbackTime") || "All"
  );
  const controllerRef = useRef(null);

  const getClaims = async () => {
    if (controllerRef.current) {
      controllerRef.current.abort(); // cancel previous request
    }
    controllerRef.current = new AbortController();
    setLoading(true);
    try {
      let requesData = {};
      if (selectedVersion) {
        requesData.versionScope =
          selectedVersion === "Latest"
            ? "latest"
            : selectedVersion === "All"
              ? "all"
              : selectedVersion;
      }
      if (selectTime !== "Custom" && selectTime !== "All") {
        const { startDate, endDate } = Utils.getDateRange(selectTime);
        requesData.fromDate = startDate;
        requesData.toDate = endDate;
      }
      if (date.startDate && date.endDate && selectTime === "Custom") {
        requesData.fromDate = date.startDate;
        requesData.toDate = date.endDate;
      }

      if (withHours !== "All") {
        requesData.isEventPublishedInWorkingHours =
          withHours === "Yes" ? true : false;
      }

      if (selectProcessedTime !== "Custom" && selectProcessedTime !== "All") {
        const { startDate, endDate } =
          Utils.getDateRangeProcessed(selectProcessedTime);
        requesData.processedOnFrom = startDate;
        requesData.processedOnTo = endDate;
      }
      if (
        processedDate.startDate &&
        processedDate.endDate &&
        selectProcessedTime === "Custom"
      ) {
        const startDateObject = new Date(
          processedDate.startDate.split("-").reverse().join("-")
        );
        const startUTCDate = Date.UTC(
          startDateObject.getUTCFullYear(),
          startDateObject.getUTCMonth(),
          startDateObject.getUTCDate(),
          0,
          0,
          0,
          0
        );
        requesData.processedOnFrom = startUTCDate;
        let endDateObject = new Date(
          processedDate.endDate.split("-").reverse().join("-")
        );
        const endUTCDate = Date.UTC(
          endDateObject.getUTCFullYear(),
          endDateObject.getUTCMonth(),
          endDateObject.getUTCDate(),
          23,
          59,
          59,
          999
        );

        requesData.processedOnTo = endUTCDate;
      }
      if (selectFeedbackTime !== "Custom" && selectFeedbackTime !== "All") {
        const { startDate, endDate } =
          Utils.getDateRangeProcessed(selectFeedbackTime);
        requesData.feedbackFromDate = startDate;
        requesData.feedbackToDate = endDate;
      }
      if (
        feedbackDate.startDate &&
        feedbackDate.endDate &&
        selectFeedbackTime === "Custom"
      ) {
        const startDateObject = new Date(
          feedbackDate.startDate.split("-").reverse().join("-")
        );
        const startUTCDate = Date.UTC(
          startDateObject.getUTCFullYear(),
          startDateObject.getUTCMonth(),
          startDateObject.getUTCDate(),
          0,
          0,
          0,
          0
        );
        requesData.feedbackFromDate = startUTCDate;
        let endDateObject = new Date(
          feedbackDate.endDate.split("-").reverse().join("-")
        );
        const endUTCDate = Date.UTC(
          endDateObject.getUTCFullYear(),
          endDateObject.getUTCMonth(),
          endDateObject.getUTCDate(),
          23,
          59,
          59,
          999
        );

        requesData.feedbackToDate = endUTCDate;
      }

      const signal = controllerRef.current.signal;
      const [error, response] = await Utils.parseResponse(
        getInspectionReport(requesData, signal)
      );

      if (error || !response) {
        return;
      }
      setReport(response);
      setLoading(false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const updateDateParams = () => {
    if (
      date?.endDate !== null &&
      date?.startDate !== null &&
      selectTime === "Custom"
    ) {
      queryParams.set("fromDate", date.startDate);
      queryParams.set("toDate", date.endDate);
    } else {
      queryParams.delete("toDate");
      queryParams.delete("fromDate");
    }
  };

  const updateProcessedParams = () => {
    if (
      processedDate?.endDate !== null &&
      processedDate?.startDate !== null &&
      selectProcessedTime === "Custom"
    ) {
      queryParams.set("processedOnFrom", processedDate.startDate);
      queryParams.set("processedOnTo", processedDate.endDate);
    } else {
      queryParams.delete("processedOnTo");
      queryParams.delete("processedOnFrom");
    }
  };

  const updateFeedbackParams = () => {
    if (
      feedbackDate?.endDate !== null &&
      feedbackDate?.startDate !== null &&
      selectFeedbackTime === "Custom"
    ) {
      queryParams.set("feedbackFromDate", feedbackDate.startDate);
      queryParams.set("feedbackToDate", feedbackDate.endDate);
    } else {
      queryParams.delete("feedbackToDate");
      queryParams.delete("feedbackFromDate");
    }
  };

  useEffect(() => {
    updateDateParams();
    queryParams.set("selectTime", selectTime);
    updateProcessedParams();
    queryParams.set("processedTime", selectProcessedTime);
    updateFeedbackParams();
    queryParams.set("feedbackTime", selectFeedbackTime);
    queryParams.set("withWorkingHours", withHours);
    queryParams.set("version", selectedVersion);
    navigate(
      {
        search: queryParams.toString()
      },
      { replace: true }
    );
    getClaims();
  }, [
    selectTime,
    selectProcessedTime,
    date,
    processedDate,
    selectFeedbackTime,
    feedbackDate,
    withHours,
    selectedVersion
  ]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <img
          onClick={() => navigate(-1)}
          src="/images/left-arrow.svg"
          alt=""
          className="cursor-pointer"
        />
        <span className="font-InterBold text-ft25-30 text-black-10">
          Repair First Report
        </span>
      </div>
      <Report
        date={date}
        processedDate={processedDate}
        report={report}
        selectProcessedTime={selectProcessedTime}
        selectTime={selectTime}
        setDate={setDate}
        setProcessedDate={setProcessedDate}
        setSelectProcessedTime={setSelectProcessedTime}
        setSelectTime={setSelectTime}
        setFeebackDate={setFeebackDate}
        feedbackDate={feedbackDate}
        setSelectFeedbackTime={setSelectFeedbackTime}
        selectFeedbackTime={selectFeedbackTime}
        loading={loading}
        withHours={withHours}
        setWithHours={setWithHours}
        setSelectedVersion={setSelectedVersion}
        selectedVersion={selectedVersion}
      />
    </div>
  );
};

export default AIReportListComponent;
